import { DataGrid } from '@mui/x-data-grid'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { getAllPromptsAdmin, getAllStyles } from '../../graphql/queries'
import ProgressBar from '../circularProgress/circularProgress'
import {
  Box,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Chip,
  Autocomplete,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Divider,
  CircularProgress
} from '@mui/material'
import React, { useState, useEffect, useMemo, useCallback, memo } from 'react'
import { useSearchParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'

const GENDER_OPTIONS = [
  { value: '', label: 'All Genders' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
]

// Move columns outside component to prevent recreation on every render
const getColumns = () => [
  {
    field: 'tableIndex',
    headerName: '#',
    flex: 0.2,
    minWidth: 80,
    filterable: false,
    sortable: false,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    minWidth: 300,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Box
        sx={{
          maxHeight: 80,
          overflowY: 'auto',
          overflowX: 'hidden',
          whiteSpace: 'pre-wrap',
          fontSize: 14,
          lineHeight: 1.5,
          width: '100%',
          pr: 1,
          fontWeight: 500,
        }}
      >
        {params.value || params.row.prompt}
      </Box>
    ),
  },
  {
    field: 'banner',
    headerName: 'Banner',
    flex: 0.8,
    minWidth: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <img
          src={params.value}
          alt="Banner"
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '12px',
            objectFit: 'cover',
            border: '2px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        />
      </Box>
    ),
  },
  {
    field: 'gender',
    headerName: 'Gender',
    flex: 0.6,
    minWidth: 100,
    renderCell: (params) => (
      <Chip
        label={params.value}
        size="small"
        sx={{
          fontWeight: 500,
          backgroundColor: params.value === 'MALE' ? '#e3f2fd' : params.value === 'FEMALE' ? '#fce4ec' : '#f5f5f5',
          color: params.value === 'MALE' ? '#1976d2' : params.value === 'FEMALE' ? '#c2185b' : '#757575',
        }}
      />
    ),
  },
  { field: 'designType', headerName: 'Design Type', flex: 0.8, minWidth: 120 },
  { field: 'toolType', headerName: 'Tool Type', flex: 1, minWidth: 150 },
  { field: 'rowNumber', headerName: 'Row #', flex: 0.4, minWidth: 80 },
  {
    field: 'isPublic',
    headerName: 'Status',
    flex: 0.6,
    minWidth: 100,
    renderCell: (params) => (
      <Chip
        label={params.value ? 'Public' : 'Private'}
        color={params.value ? 'success' : 'default'}
        size="small"
        sx={{ fontWeight: 500 }}
      />
    ),
  },
]

function PromptTable() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  // State management
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 0)
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize')) || 50)
  const [selectedGender, setSelectedGender] = useState('')
  const [selectedStyle, setSelectedStyle] = useState(null)
  const [searchName, setSearchName] = useState('')
  const [debouncedSearchName, setDebouncedSearchName] = useState('')

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchName(searchName)
      if (searchName !== debouncedSearchName) {
        setPage(0)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchName, debouncedSearchName])

  // Scroll position restoration
  useEffect(() => {
    if (location.state?.preserveScroll) {
      const savedScrollPosition = sessionStorage.getItem('promptTableScrollPosition')
      if (savedScrollPosition) {
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollPosition))
        }, 300)
      }
      navigate(location.pathname + location.search, {
        replace: true,
        state: {},
      })
    }
  }, [location.state, navigate, location.pathname, location.search])

  // Save scroll position
  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem('promptTableScrollPosition', window.scrollY.toString())
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Update URL params
  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    })
  }, [page, pageSize, setSearchParams])

  // Build query variables
  const variables = useMemo(() => {
    const vars = {
      page: page + 1,
      pageSize,
    }

    if (selectedGender) vars.gender = selectedGender
    if (selectedStyle) vars.styleId = selectedStyle
    if (debouncedSearchName) vars.name = debouncedSearchName

    return vars
  }, [page, pageSize, selectedGender, selectedStyle, debouncedSearchName])

  // Queries with optimized fetch policies
  const { loading, error, data } = useQuery(getAllPromptsAdmin, {
    variables,
    fetchPolicy: 'cache-and-network', // Use cache first, then update with network
    notifyOnNetworkStatusChange: false, // Don't trigger re-render on network status changes
  })
  const { data: stylesData } = useQuery(getAllStyles, {
    fetchPolicy: 'cache-first', // Styles don't change often, use cache
  })

  // Event handlers
  const handleRowClick = useCallback(
    (params) => {
      sessionStorage.setItem('promptTableScrollPosition', window.scrollY.toString())
      navigate(`/prompts/${params.id}`)
    },
    [navigate]
  )

  const handlePaginationModelChange = useCallback((newModel) => {
    setPage(newModel.page)
    setPageSize(newModel.pageSize)
  }, [])

  const handleClearFilters = useCallback(() => {
    setSelectedGender('')
    setSelectedStyle(null)
    setSearchName('')
    setDebouncedSearchName('')
    setPage(0)
  }, [])

  const hasActiveFilters = selectedGender || selectedStyle || debouncedSearchName

  // Memoize columns to prevent recreation
  const columns = useMemo(() => getColumns(), [])

  // Data processing
  const rowsData = useMemo(() => data?.getAllPromptsAdmin?.prompts ?? [], [data])
  const totalCount = useMemo(() => data?.getAllPromptsAdmin?.totalCount ?? 0, [data])
  const styles = useMemo(() => stylesData?.getAllStylesAdmin?.styles || [], [stylesData])

  const rowsWithIndex = useMemo(
    () =>
      rowsData.map((row, index) => ({
        ...row,
        tableIndex: page * pageSize + index + 1,
      })),
    [rowsData, page, pageSize]
  )

  if (loading) return <ProgressBar />
  if (error) return <Typography color="error">Error: {error.message}</Typography>

  return (
    <Box sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon sx={{ fontSize: 28, color: '#1976d2' }} />
          <Typography variant="h5" fontWeight="600" color="#2c3e50">
            Prompts Management
          </Typography>
        </Box>
        <Link to="/prompts/create" style={{ textDecoration: 'none' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#1976d2',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                backgroundColor: '#1565c0',
                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
              },
            }}
          >
            Create New Prompt
          </Button>
        </Link>
      </Box>

      {/* Filters Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <FilterListIcon sx={{ color: '#757575' }} />
          <Typography variant="h6" fontWeight="600" color="#424242">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label="Active"
              size="small"
              color="primary"
              sx={{ ml: 1, fontWeight: 500 }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 3,
            }}
          >
            {/* Search Input */}
            <FormControl fullWidth>
              <TextField
                label="Search by Name"
                variant="outlined"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="Type to search..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#9e9e9e', mr: 1 }} />,
                  endAdornment: (
                    <>
                      {searchName !== debouncedSearchName && loading && (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      )}
                      {searchName && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchName('')
                            setDebouncedSearchName('')
                            setPage(0)
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </FormControl>

            {/* Gender Select */}
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={selectedGender}
                onChange={(e) => {
                  setSelectedGender(e.target.value)
                  setPage(0)
                }}
                label="Gender"
                sx={{
                  borderRadius: 2,
                }}
              >
                {GENDER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Style Autocomplete */}
            <FormControl fullWidth>
              <Autocomplete
                options={styles}
                getOptionLabel={(option) => option.name || ''}
                value={styles.find((style) => style.id === selectedStyle) || null}
                onChange={(event, newValue) => {
                  setSelectedStyle(newValue ? newValue.id : null)
                  setPage(0)
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Style" placeholder="Select a style..." />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </FormControl>
          </Box>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  color: '#757575',
                  borderColor: '#e0e0e0',
                  '&:hover': {
                    borderColor: '#bdbdbd',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Clear All Filters
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Results Summary */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Showing <strong>{rowsWithIndex.length}</strong> of <strong>{totalCount}</strong> prompts
        </Typography>
      </Box>

      {/* Data Grid */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <DataGrid
          disableColumnMenu
          rows={rowsWithIndex}
          columns={columns}
          rowHeight={110}
          rowCount={totalCount}
          paginationMode="server"
          onRowClick={handleRowClick}
          loading={loading}
          pagination
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[25, 50, 100]}
          autoHeight
          disableVirtualization
          sx={{
            border: 'none',
            fontSize: '14px',
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#f5f9ff',
              },
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#fafafa',
              borderBottom: '2px solid #e0e0e0',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '14px',
              color: '#424242',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '2px solid #e0e0e0',
              backgroundColor: '#fafafa',
            },
          }}
        />
      </Paper>
    </Box>
  )
}

// Export memoized component to prevent unnecessary re-renders
export default memo(PromptTable)
